// Shopify Forms Shadow DOM Styler - Class-based approach for App Blocks
// This script provides a class to inject custom styles into Shopify Forms shadow roots

// Global registry to track styled shadow roots
if (typeof window !== 'undefined') {
  window.ShopifyFormsStylerRegistry =
    window.ShopifyFormsStylerRegistry || new WeakMap();
}

class ShopifyFormsStyler {
  constructor(options = {}) {
    // Simplified color configuration with primary color system
    this.colors = {
      // Primary color system
      primary: options.primary || '#bd0a45',
      primaryHover: options.primaryHover || '#a6093d',
      primaryActive: options.primaryActive || '#950836',

      // Background colors
      backgroundColor: options.backgroundColor || '#f5f5f5',
      containerBackground: options.containerBackground || 'transparent',

      // Text colors (inherit from primary but allow overrides)
      textColor: options.textColor || options.primary || '#202020',
      labelColor: options.labelColor || options.primary || '#202020',

      // Button colors with extended options
      buttonText: options.buttonText || '#ffffff',
      buttonBackground:
        options.buttonBackground || options.primary || '#bd0a45',
      buttonHoverBackground:
        options.buttonHoverBackground || options.primaryHover || '#a6093d',
      buttonHoverText:
        options.buttonHoverText || options.buttonText || '#ffffff',

      // Input field colors
      inputBackground:
        options.inputBackground || options.backgroundColor || '#f5f5f5',
      inputText:
        options.inputText || options.textColor || options.primary || '#202020',

      // Error and success colors
      errorText: options.errorText || '#dc2626',
      successText: options.successText || '#10b981',

      // Placeholder and disclaimer
      placeholderText: options.placeholderText || '#20202099',
      disclaimerText: options.disclaimerText || '#202020cc',
    };

    // Layout and alignment options
    this.layout = {
      // Container alignment and padding
      containerAlignment: options.containerAlignment || 'left',
      containerPaddingTop: options.containerPaddingTop || '0px',
      containerPaddingRight: options.containerPaddingRight || '0px',
      containerPaddingBottom: options.containerPaddingBottom || '0px',
      containerPaddingLeft: options.containerPaddingLeft || '0px',

      // Form alignment and padding
      formAlignment: options.formAlignment || 'left',
      formPaddingTop: options.formPaddingTop || '10px',
      formPaddingRight: options.formPaddingRight || '10px',
      formPaddingBottom: options.formPaddingBottom || '10px',
      formPaddingLeft: options.formPaddingLeft || '10px',

      // Form container max width and parent alignment
      formMaxWidth: options.formMaxWidth || '0',
      formParentAlignment: options.formParentAlignment || 'left',

      // Error message alignment
      errorAlignment: options.errorAlignment || 'left',
      errorPaddingTop: options.errorPaddingTop || '5px',
      errorPaddingBottom: options.errorPaddingBottom || '5px',

      // Field and button padding
      fieldPadding: options.fieldPadding || '15px',
      buttonPadding: options.buttonPadding || '15px',
    };

    this.appBlockElement = options.appBlockElement || document.body;
    this.formInstanceId =
      options.formInstanceId || Math.random().toString(36).substr(2, 9);
    this.observer = null;
    this.styleId = `shopify-forms-custom-styles-${this.formInstanceId}`;

    this.init();
  }

  // Extract and convert color values to hex format when needed
  extractHexColor(colorValue) {
    if (!colorValue) return '#bd0a45';

    // Handle HSL format - convert to hex for box-shadow compatibility
    const hslMatch = colorValue.match(
      /hsl\((\d+)\s+(\d+)%\s+(\d+)%(?:\s*\/\s*([0-9.]+))?\)/,
    );
    if (hslMatch) {
      const h = parseInt(hslMatch[1]);
      const s = parseInt(hslMatch[2]);
      const l = parseInt(hslMatch[3]);
      const a = hslMatch[4] ? parseFloat(hslMatch[4]) : 1;

      // Convert HSL to RGB
      const hslToRgb = (h, s, l) => {
        h /= 360;
        s /= 100;
        l /= 100;
        const k = (n) => (n + h / (1 / 12)) % 1;
        const a = s * Math.min(l, 1 - l);
        const f = (n) =>
          l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
        return [
          Math.round(255 * f(0)),
          Math.round(255 * f(8)),
          Math.round(255 * f(4)),
        ];
      };

      const [r, g, b] = hslToRgb(h, s, l);

      // Convert RGB to hex
      const toHex = (n) => n.toString(16).padStart(2, '0');
      const hex = `#${toHex(r)}${toHex(g)}${toHex(b)}`;

      // Handle alpha transparency
      if (a < 1) {
        const alphaHex = Math.round(a * 255)
          .toString(16)
          .padStart(2, '0');
        return `${hex}${alphaHex}`;
      }

      return hex;
    }

    // Handle hex format - just return as-is
    const hexMatch = colorValue.match(
      /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/,
    );
    if (hexMatch) {
      return hexMatch[0];
    }

    // Handle RGB format rgb(r, g, b)
    const rgbMatch = colorValue.match(
      /rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/,
    );
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
      const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
      const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
      return `#${r}${g}${b}`;
    }

    // Default fallbacks
    if (colorValue.includes('connoisseurs-red')) {
      return '#bd0a45';
    }
    if (colorValue.includes('neutral-darkest')) {
      return '#202020';
    }
    if (colorValue.includes('neutral-lightest')) {
      return '#f5f5f5';
    }

    // Error color fallback
    if (colorValue.includes('#dc2626') || colorValue.includes('#e02229')) {
      return '#dc2626';
    }

    // Success color fallback
    if (colorValue.includes('#10b981')) {
      return '#10b981';
    }

    // Final fallback - return original if no conversion possible
    return colorValue.startsWith('#') ? colorValue : '#bd0a45';
  }

  // Check if shadow root is already claimed by another instance
  isShadowRootClaimed(shadowRoot) {
    if (!window.ShopifyFormsStylerRegistry) return false;
    return window.ShopifyFormsStylerRegistry.has(shadowRoot);
  }

  // Claim ownership of a shadow root
  claimShadowRoot(shadowRoot) {
    if (!window.ShopifyFormsStylerRegistry) return false;
    if (this.isShadowRootClaimed(shadowRoot)) {
      return false; // Already claimed by another instance
    }
    window.ShopifyFormsStylerRegistry.set(shadowRoot, this.formInstanceId);
    return true;
  }

  // Check if this instance owns the shadow root
  ownsShadowRoot(shadowRoot) {
    if (!window.ShopifyFormsStylerRegistry) return false;
    return (
      window.ShopifyFormsStylerRegistry.get(shadowRoot) === this.formInstanceId
    );
  }

  // Release ownership of a shadow root
  releaseShadowRoot(shadowRoot) {
    if (!window.ShopifyFormsStylerRegistry) return;
    if (this.ownsShadowRoot(shadowRoot)) {
      window.ShopifyFormsStylerRegistry.delete(shadowRoot);
    }
  }

  // Generate CSS variable overrides for the shadow DOM
  generateCSSVariableOverrides(forShadowDOM = false) {
    // Only generate CSS variable overrides for shadow DOM
    if (!forShadowDOM) {
      return '';
    }

    // Get hex versions ONLY for box-shadow compatibility
    const primaryHex = this.extractHexColor(this.colors.primary);
    const errorHex = this.extractHexColor(this.colors.errorText);

    return `
      :host {
        /* Color overrides with !important to supersede existing declarations */
        --forms-accent-color: ${primaryHex} !important;
        --forms-accent-color-hovered-modifier: ${primaryHex}1a !important;
        --forms-discount-code-color: ${primaryHex} !important;
        --body-text-color: ${this.colors.textColor} !important;
        --link-text-color: ${this.colors.primary} !important;
        --heading-text-color: ${this.colors.labelColor} !important;
        --form-error-color: ${this.colors.errorText} !important;
        --form-text-color: ${this.colors.inputText} !important;
        --form-placeholder-color: ${this.colors.placeholderText} !important;
        --form-background-color: ${this.colors.inputBackground} !important;
        --button-text-color: ${this.colors.buttonText} !important;
        --button-background-color: ${this.colors.buttonBackground} !important;
        --button-background-color-hovered-modifier: opacity(90%) !important;
        --container-background-color: ${
          this.colors.containerBackground === 'transparent'
            ? 'transparent'
            : this.colors.containerBackground
        } !important;
        
        /* Border overrides using box-shadow (this is how Shopify Forms creates "borders") */
        --form-field-border: 0 0 0 1px ${primaryHex}33 !important;
        --form-field-border-focused: 0 0 0 2px ${primaryHex} !important;
        --button-border: 0px solid none !important;
        
        /* Layout overrides */
        --forms-padding-top: ${this.layout.formPaddingTop} !important;
        --forms-padding-right: ${this.layout.formPaddingRight} !important;
        --forms-padding-bottom: ${this.layout.formPaddingBottom} !important;
        --forms-padding-left: ${this.layout.formPaddingLeft} !important;
        --container-text-alignment: ${
          this.layout.containerAlignment
        } !important;
        
        /* Max width overrides */
        --inline-container-max-width: ${
          this.layout.formMaxWidth === '0' || this.layout.formMaxWidth === 0
            ? 'none'
            : this.layout.formMaxWidth + 'px'
        } !important;
        --form-container-max-width: ${
          this.layout.formMaxWidth === '0' || this.layout.formMaxWidth === 0
            ? 'none'
            : this.layout.formMaxWidth + 'px'
        } !important;
        --container-max-width: ${
          this.layout.formMaxWidth === '0' || this.layout.formMaxWidth === 0
            ? 'none'
            : this.layout.formMaxWidth + 'px'
        } !important;
        
        /* Field and button padding */
        --form-field-padding: ${this.layout.fieldPadding} !important;
        --button-padding: ${this.layout.buttonPadding} !important;
        
        /* Border radius overrides */
        --form-field-border-radius: 0px !important;
        --button-border-radius: 0px !important;
        --container-border-radius: 0px !important;
      }
    `;
  }

  // Generate CSS styles using configured colors and layout
  generateFormStyles(forShadowDOM = false) {
    // Base prefix for selectors - empty for shadow DOM, includes container for light DOM
    const selectorPrefix = forShadowDOM ? '' : 'div[data-form-root="true"] ';

    // Get hex versions ONLY for box-shadow compatibility
    const primaryHex = this.extractHexColor(this.colors.primary);
    const primaryHoverHex = this.extractHexColor(this.colors.primaryHover);
    const primaryActiveHex = this.extractHexColor(this.colors.primaryActive);

    return `
      /* CSS Variable Overrides */
      ${this.generateCSSVariableOverrides(forShadowDOM)}

      /* Container styling and alignment */
      ${selectorPrefix}${
      forShadowDOM ? ':host' : 'div[data-form-root="true"]'
    } {
        background-color: ${this.colors.containerBackground};
        text-align: ${this.layout.containerAlignment};
        padding-top: ${this.layout.containerPaddingTop};
        padding-right: ${this.layout.containerPaddingRight};
        padding-bottom: ${this.layout.containerPaddingBottom};
        padding-left: ${this.layout.containerPaddingLeft};
      }

      /* Form container styling */
      ${selectorPrefix}section {
        width: unset;
        text-align: ${this.layout.formParentAlignment};
        max-width: ${
          this.layout.formMaxWidth === '0' || this.layout.formMaxWidth === 0
            ? 'none'
            : this.layout.formMaxWidth + 'px'
        } !important;
      }

      ${selectorPrefix}section section[data-testid="form-container"] {
        width: 100%;
        max-width: ${
          this.layout.formMaxWidth === '0' || this.layout.formMaxWidth === 0
            ? 'none'
            : this.layout.formMaxWidth + 'px'
        } !important;
        margin-left: ${
          this.layout.formParentAlignment === 'center'
            ? 'auto'
            : this.layout.formParentAlignment === 'right'
            ? 'auto'
            : '0'
        };
        margin-right: ${
          this.layout.formParentAlignment === 'center'
            ? 'auto'
            : this.layout.formParentAlignment === 'left'
            ? 'auto'
            : '0'
        };
        padding-top: ${this.layout.formPaddingTop};
        padding-right: ${this.layout.formPaddingRight};
        padding-bottom: ${this.layout.formPaddingBottom};
        padding-left: ${this.layout.formPaddingLeft};
        text-align: ${this.layout.formAlignment};
      }

      /* Additional max-width controls for various form containers */
      ${selectorPrefix}form[data-testid="form"],
      ${selectorPrefix}form {
        max-width: ${
          this.layout.formMaxWidth === '0' || this.layout.formMaxWidth === 0
            ? 'none'
            : this.layout.formMaxWidth + 'px'
        } !important;
        text-align: ${this.layout.formAlignment};
      }

      /* Input field styling with box-shadow borders */
      ${selectorPrefix}form[data-testid="form"] input[type="text"],
      ${selectorPrefix}form[data-testid="form"] input[inputmode="text"],
      ${selectorPrefix}form[data-testid="form"] input[inputmode="email"],
      ${selectorPrefix}form[data-testid="form"] input[data-testid*="field-"],
      ${selectorPrefix}form input[type="text"],
      ${selectorPrefix}form input[type="email"],
      ${selectorPrefix}form input[type="tel"],
      ${selectorPrefix}form input[type="url"],
      ${selectorPrefix}form input[type="number"] {
        background-color: ${this.colors.inputBackground} !important;
        color: ${this.colors.inputText} !important;
        box-shadow: 0 0 0 1px ${primaryHex}33 !important;
        border: none !important;
        border-radius: 0 !important;
        text-align: left;
      }

      /* Input focus state with box-shadow */
      ${selectorPrefix}form[data-testid="form"] input[type="text"]:focus,
      ${selectorPrefix}form[data-testid="form"] input[inputmode="text"]:focus,
      ${selectorPrefix}form[data-testid="form"] input[inputmode="email"]:focus,
      ${selectorPrefix}form[data-testid="form"] input[data-testid*="field-"]:focus,
      ${selectorPrefix}form input[type="text"]:focus,
      ${selectorPrefix}form input[type="email"]:focus,
      ${selectorPrefix}form input[type="tel"]:focus,
      ${selectorPrefix}form input[type="url"]:focus,
      ${selectorPrefix}form input[type="number"]:focus {
        box-shadow: 0 0 0 2px ${primaryHex} !important;
        outline: none !important;
      }

      /* Input active state with box-shadow */
      ${selectorPrefix}form[data-testid="form"] input[type="text"]:active,
      ${selectorPrefix}form[data-testid="form"] input[inputmode="text"]:active,
      ${selectorPrefix}form[data-testid="form"] input[inputmode="email"]:active,
      ${selectorPrefix}form[data-testid="form"] input[data-testid*="field-"]:active,
      ${selectorPrefix}form input[type="text"]:active,
      ${selectorPrefix}form input[type="email"]:active,
      ${selectorPrefix}form input[type="tel"]:active,
      ${selectorPrefix}form input[type="url"]:active,
      ${selectorPrefix}form input[type="number"]:active {
        box-shadow: 0 0 0 2px ${primaryActiveHex} !important;
      }

      /* Textarea styling with box-shadow borders */
      ${selectorPrefix}form[data-testid="form"] textarea,
      ${selectorPrefix}form textarea {
        background-color: ${this.colors.inputBackground} !important;
        color: ${this.colors.inputText} !important;
        box-shadow: 0 0 0 1px ${primaryHex}33 !important;
        border: none !important;
        border-radius: 0 !important;
      }

      ${selectorPrefix}form[data-testid="form"] textarea:focus,
      ${selectorPrefix}form textarea:focus {
        box-shadow: 0 0 0 2px ${primaryHex} !important;
        outline: none !important;
      }

      ${selectorPrefix}form[data-testid="form"] textarea:active,
      ${selectorPrefix}form textarea:active {
        box-shadow: 0 0 0 2px ${primaryActiveHex} !important;
      }

      /* Select styling with box-shadow borders */
      ${selectorPrefix}form[data-testid="form"] select,
      ${selectorPrefix}form select {
        background-color: ${this.colors.inputBackground} !important;
        color: ${this.colors.inputText} !important;
        box-shadow: 0 0 0 1px ${primaryHex}33 !important;
        border: none !important;
        border-radius: 0 !important;
      }

      ${selectorPrefix}form[data-testid="form"] select:focus,
      ${selectorPrefix}form select:focus {
        box-shadow: 0 0 0 2px ${primaryHex} !important;
        outline: none !important;
      }

      ${selectorPrefix}form[data-testid="form"] select:active,
      ${selectorPrefix}form select:active {
        box-shadow: 0 0 0 2px ${primaryActiveHex} !important;
      }

      /* Label styling */
      ${selectorPrefix}form[data-testid="form"] label[id*="label-"],
      ${selectorPrefix}form[data-testid="form"] label,
      ${selectorPrefix}form label {
        color: ${this.colors.labelColor} !important;
        text-align: left;
      }

      /* Submit button styling with HSL colors */
      ${selectorPrefix}form[data-testid="form"] button[data-testid="btn-form-submit"],
      ${selectorPrefix}form[data-testid="form"] button[type="submit"],
      ${selectorPrefix}form[data-testid="form"] input[type="submit"],
      ${selectorPrefix}form input[type="submit"],
      ${selectorPrefix}form button[type="submit"],
      ${selectorPrefix}form button.submit-button {
        background-color: ${this.colors.buttonBackground} !important;
        color: ${this.colors.buttonText} !important;
        border: none !important;
        border-radius: 0 !important;
      }

      /* Button hover state */
      ${selectorPrefix}form[data-testid="form"] button[data-testid="btn-form-submit"]:hover,
      ${selectorPrefix}form[data-testid="form"] button[type="submit"]:hover,
      ${selectorPrefix}form[data-testid="form"] input[type="submit"]:hover,
      ${selectorPrefix}form input[type="submit"]:hover,
      ${selectorPrefix}form button[type="submit"]:hover,
      ${selectorPrefix}form button.submit-button:hover {
        background-color: ${this.colors.buttonHoverBackground} !important;
        color: ${this.colors.buttonHoverText} !important;
      }

      /* Button active state */
      ${selectorPrefix}form[data-testid="form"] button[data-testid="btn-form-submit"]:active,
      ${selectorPrefix}form[data-testid="form"] button[type="submit"]:active,
      ${selectorPrefix}form[data-testid="form"] input[type="submit"]:active,
      ${selectorPrefix}form input[type="submit"]:active,
      ${selectorPrefix}form button[type="submit"]:active,
      ${selectorPrefix}form button.submit-button:active {
        background-color: ${this.colors.buttonHoverBackground} !important;
        color: ${this.colors.buttonHoverText} !important;
        opacity: 0.9;
      }

      /* Checkbox and radio styling with box-shadow */
      ${selectorPrefix}form[data-testid="form"] input[type="checkbox"],
      ${selectorPrefix}form[data-testid="form"] input[type="radio"],
      ${selectorPrefix}form input[type="checkbox"],
      ${selectorPrefix}form input[type="radio"] {
        background-color: ${this.colors.inputBackground} !important;
        box-shadow: 0 0 0 1px ${primaryHex}33 !important;
        border: none !important;
      }

      ${selectorPrefix}form[data-testid="form"] input[type="checkbox"]:checked,
      ${selectorPrefix}form[data-testid="form"] input[type="radio"]:checked,
      ${selectorPrefix}form input[type="checkbox"]:checked,
      ${selectorPrefix}form input[type="radio"]:checked {
        background-color: ${this.colors.buttonBackground} !important;
        box-shadow: 0 0 0 1px ${primaryHex} !important;
      }

      ${selectorPrefix}form[data-testid="form"] input[type="radio"],
      ${selectorPrefix}form input[type="radio"] {
        border-radius: 50% !important;
      }

      /* Error message styling with alignment options */
      ${selectorPrefix}.error,
      ${selectorPrefix}.form-error,
      ${selectorPrefix}[class*="error"],
      ${selectorPrefix}[data-testid*="error"],
      ${selectorPrefix}output[role="alert"],
      ${selectorPrefix}output[class*="formError"] {
        color: ${this.colors.errorText} !important;
        text-align: ${this.layout.errorAlignment};
        padding-top: ${this.layout.errorPaddingTop};
        padding-bottom: ${this.layout.errorPaddingBottom};
        padding-left: 0;
        padding-right: 0;
        margin: 0;
      }

      /* Success message styling */
      ${selectorPrefix}.success,
      ${selectorPrefix}.form-success,
      ${selectorPrefix}[data-testid*="success"] {
        color: ${this.colors.successText} !important;
        text-align: ${this.layout.errorAlignment};
        border-radius: 0;
      }

      /* Required field indicators */
      ${selectorPrefix}.required::after,
      ${selectorPrefix}[required] + label::after {
        color: ${this.colors.primary} !important;
      }

      /* Placeholder styling */
      ${selectorPrefix}form[data-testid="form"] input::placeholder,
      ${selectorPrefix}form[data-testid="form"] textarea::placeholder,
      ${selectorPrefix}form input::placeholder,
      ${selectorPrefix}form textarea::placeholder {
        color: ${this.colors.placeholderText} !important;
      }

      /* Disclaimer styling */
      ${selectorPrefix}div[class*="formDisclaimer"] p,
      ${selectorPrefix}.form-disclaimer,
      ${selectorPrefix}[data-testid*="disclaimer"] {
        color: ${this.colors.disclaimerText} !important;
        text-align: ${this.layout.formAlignment};
      }
    `;
  }

  // Function to inject styles into shadow DOM
  injectStylesIntoShadowRoot(shadowRoot) {
    // Check if this shadow root is already claimed by another instance
    if (
      this.isShadowRootClaimed(shadowRoot) &&
      !this.ownsShadowRoot(shadowRoot)
    ) {
      console.log(
        `ShopifyFormsStyler: Shadow root already claimed by another instance. Skipping styling for ${this.formInstanceId}`,
      );
      return;
    }

    // Check if our specific styles are already injected
    if (shadowRoot.querySelector(`#${this.styleId}`)) {
      return;
    }

    // Try to claim ownership of this shadow root
    if (!this.claimShadowRoot(shadowRoot)) {
      console.log(
        `ShopifyFormsStyler: Failed to claim shadow root for ${this.formInstanceId}`,
      );
      return;
    }

    // Create style element
    const styleElement = document.createElement('style');
    styleElement.id = this.styleId;
    styleElement.textContent = this.generateFormStyles(true); // true for shadow DOM

    // Insert at the beginning of shadow root
    shadowRoot.insertBefore(styleElement, shadowRoot.firstChild);

    console.log(
      `ShopifyFormsStyler: Successfully styled shadow root for ${this.formInstanceId}`,
    );
  }

  // Function to find and style form elements within the app block
  styleShopifyForms() {
    // Only look for form-embed elements within this specific app block
    if (!this.appBlockElement) {
      console.warn('ShopifyFormsStyler: No app block element found');
      return;
    }

    // Look for form-embed elements ONLY within this app block
    const formEmbeds = this.appBlockElement.querySelectorAll('form-embed');

    formEmbeds.forEach((embed) => {
      if (embed.shadowRoot) {
        this.injectStylesIntoShadowRoot(embed.shadowRoot);
      }
    });

    // Also check for any elements with data-form-root attribute that might have shadow roots
    // but ONLY within this app block
    const formRoots = this.appBlockElement.querySelectorAll(
      '[data-form-root="true"]',
    );

    formRoots.forEach((root) => {
      // Check if any child elements have shadow roots
      const shadowElements = root.querySelectorAll('*');
      shadowElements.forEach((element) => {
        if (element.shadowRoot) {
          this.injectStylesIntoShadowRoot(element.shadowRoot);
        }
      });
    });
  }

  // Initialize the styler for this app block
  init() {
    // Ensure we have a valid app block element
    if (!this.appBlockElement) {
      console.error('ShopifyFormsStyler: Invalid app block element provided');
      return;
    }

    console.log(
      `ShopifyFormsStyler: Initializing for instance ${this.formInstanceId}`,
    );

    // Style existing forms within this app block only
    this.styleShopifyForms();

    // Observer to watch for dynamically added forms within this specific app block
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Check if the added node is a form element or contains form elements
              // and is within our app block
              if (
                this.appBlockElement.contains(node) &&
                (node.tagName === 'FORM-EMBED' ||
                  (node.querySelector && node.querySelector('form-embed')))
              ) {
                setTimeout(() => this.styleShopifyForms(), 100); // Small delay to ensure shadow DOM is ready
              }
            }
          });
        }
      });
    });

    // Start observing for dynamic content within this specific app block only
    this.observer.observe(this.appBlockElement, {
      childList: true,
      subtree: true,
    });

    // Also try again after short delays in case forms load asynchronously
    setTimeout(() => this.styleShopifyForms(), 1000);
    setTimeout(() => this.styleShopifyForms(), 3000);
  }

  // Update colors and layout options and regenerate styles
  updateOptions(newOptions) {
    // Update colors if provided
    if (newOptions.colors) {
      this.colors = { ...this.colors, ...newOptions.colors };
    }

    // Update layout if provided
    if (newOptions.layout) {
      this.layout = { ...this.layout, ...newOptions.layout };
    }

    // For backward compatibility, support direct color properties
    Object.keys(newOptions).forEach((key) => {
      if (key !== 'colors' && key !== 'layout') {
        // Check if it's a color property
        if (this.colors.hasOwnProperty(key)) {
          this.colors[key] = newOptions[key];
        }
        // Check if it's a layout property
        else if (this.layout.hasOwnProperty(key)) {
          this.layout[key] = newOptions[key];
        }
      }
    });

    // Only re-style forms within this app block
    this.styleShopifyForms();
  }

  // Backward compatibility method
  updateColors(newColors) {
    this.updateOptions({ colors: newColors });
  }

  // Destroy the styler and clean up observers
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Remove injected styles from shadow roots within this app block only
    if (this.appBlockElement) {
      const formEmbeds = this.appBlockElement.querySelectorAll('form-embed');
      formEmbeds.forEach((embed) => {
        if (embed.shadowRoot && this.ownsShadowRoot(embed.shadowRoot)) {
          const styleElement = embed.shadowRoot.querySelector(
            `#${this.styleId}`,
          );
          if (styleElement) {
            styleElement.remove();
          }
          // Release ownership of the shadow root
          this.releaseShadowRoot(embed.shadowRoot);
        }
      });

      // Also clean up any other shadow roots we might own
      const formRoots = this.appBlockElement.querySelectorAll(
        '[data-form-root="true"]',
      );
      formRoots.forEach((root) => {
        const shadowElements = root.querySelectorAll('*');
        shadowElements.forEach((element) => {
          if (element.shadowRoot && this.ownsShadowRoot(element.shadowRoot)) {
            const styleElement = element.shadowRoot.querySelector(
              `#${this.styleId}`,
            );
            if (styleElement) {
              styleElement.remove();
            }
            this.releaseShadowRoot(element.shadowRoot);
          }
        });
      });
    }

    console.log(
      `ShopifyFormsStyler: Destroyed instance ${this.formInstanceId}`,
    );
  }
}

// Make the class available globally
if (typeof window !== 'undefined') {
  window.ShopifyFormsStyler = ShopifyFormsStyler;
}
